// RevenueTicker.jsx - Floating revenue display
// Shows "Today's Revenue: $X,XXX,XXX" with delta flash on updates
// Resets at 22:00 game time (handled by EndOfDaySystem)

import React, { useState, useEffect, useRef } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
// Icons removed - text-only UI

// Format currency
const formatMoney = (amount) => {
  if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000)}K`;
  return `$${Math.round(amount)}`;
};

export default function RevenueTicker({ position = "header" }) {
  const { worldState } = useWorldState();
  const dailyRevenue = worldState?.stats?.dailyRevenue || 0;

  // Track previous revenue to show delta
  const prevRevenueRef = useRef(dailyRevenue);
  const [delta, setDelta] = useState(0);
  const [showDelta, setShowDelta] = useState(false);

  // Detect revenue changes and show delta
  useEffect(() => {
    const prevRevenue = prevRevenueRef.current;

    if (dailyRevenue > prevRevenue) {
      const newDelta = dailyRevenue - prevRevenue;
      setDelta(newDelta);
      setShowDelta(true);

      // Hide delta after 2 seconds
      const timer = setTimeout(() => {
        setShowDelta(false);
      }, 2000);

      prevRevenueRef.current = dailyRevenue;
      return () => clearTimeout(timer);
    }

    // Handle reset (new day)
    if (dailyRevenue < prevRevenue) {
      prevRevenueRef.current = dailyRevenue;
      setDelta(0);
      setShowDelta(false);
    }
  }, [dailyRevenue]);

  // Floating position style
  const floatingStyle = position === "floating" ? {
    position: "fixed",
    top: "16px",
    right: "16px",
    zIndex: 40,
  } : {};

  return (
    <div
      className="flex items-center gap-3 px-4 py-2 rounded-lg"
      style={{
        backgroundColor: "#0c0c0c",
        border: "1px solid #1a1a1a",
        ...floatingStyle,
      }}
    >
      {/* Main revenue display */}
      <div>
        <div className="text-xs uppercase" style={{ color: "#525252" }}>
          $ Today's Revenue
        </div>
        <div
          className="text-lg font-bold tabular-nums"
          style={{ color: "#22c55e" }}
        >
          {formatMoney(dailyRevenue)}
        </div>
      </div>

      {/* Delta flash */}
      {showDelta && delta > 0 && (
        <div
          className="px-2 py-1 rounded animate-pulse"
          style={{
            backgroundColor: "#052e16",
            border: "1px solid #22c55e",
          }}
        >
          <span
            className="text-sm font-bold"
            style={{ color: "#22c55e" }}
          >
            ↑ +{formatMoney(delta)}
          </span>
        </div>
      )}
    </div>
  );
}

// Compact version for tight spaces
export function RevenueTickerCompact() {
  const { worldState } = useWorldState();
  const dailyRevenue = worldState?.stats?.dailyRevenue || 0;

  return (
    <div className="flex items-center gap-1">
      <span className="text-sm font-bold tabular-nums" style={{ color: "#22c55e" }}>
        ${formatMoney(dailyRevenue)}
      </span>
      <span className="text-xs" style={{ color: "#525252" }}>today</span>
    </div>
  );
}
